import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../csr-web-api.service';
import { AuthSessionService } from '../../../core/auth-session.service';
import {
  ApiErrorShape,
  CareerRolePopulationRunResponse,
  PromotionEntity,
  NotificationResponse,
  ReconcileYouTubeTutorialLanguagesResponse
} from '../../../models';
import { AnalyticsDashboardComponent } from '../analytics-dashboard/analytics-dashboard.component';
import { AnnouncementsPageComponent } from '../announcements-page/announcements-page.component';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatToolbarModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    MatTabsModule,
    MatProgressBarModule,
    MatChipsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatDividerModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    AnalyticsDashboardComponent,
    AnnouncementsPageComponent
  ],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.scss'
})
export class AdminDashboardComponent implements OnInit {
  protected readonly auth = inject(AuthSessionService);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(CsrWebApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);

  loading = false;
  lastResponse = 'No response yet.';
  lastError = '';
  lastRun: CareerRolePopulationRunResponse | null = null;
  listedRuns: CareerRolePopulationRunResponse[] = [];
  listedPromotions: PromotionEntity[] = [];
  selectedPromotion: PromotionEntity | null = null;
  activeAlerts: NotificationResponse[] = [];
  latestSweepRun: CareerRolePopulationRunResponse | null = null;
  latestIngestionBadgeUpdatedAt: Date | null = null;
  lastYouTubeLanguageReconcile: ReconcileYouTubeTutorialLanguagesResponse | null = null;
  protected readonly runStatuses = ['queued', 'in_progress', 'completed', 'failed'];
  protected readonly triggerTypes = ['promo_code', 'automatic'];
  protected readonly promotionTypes = ['percentage', 'fixed_amount'];
  protected readonly eligibilityTypes = ['all_users', 'first_purchase_only', 'specific_user_tags'];
  protected readonly serviceTypes = [
    'career_path_finder',
    'skill_gap_analysis',
    'targeted_skill_gap_analysis',
    'learning_plan',
    'learning_plan_reassessment',
    'counselling_session',
    'coaching_session',
    'mock_interview'
  ];

  readonly supportLinks = [
    { label: 'Learning effectiveness & submission investigations', path: '/admin/learning-effectiveness' },
    { label: 'Learner Management', path: '/admin/learner-management' },
    { label: 'Support queues', path: '/admin/support' },
    { label: 'Learning Plan Preview Diagnostics', path: '/admin/learning-plan-previews' },
    { label: 'Exceptions', path: '/admin/exceptions' }
  ];

  readonly toolsLinks = [
    { label: 'Legal documents', path: '/admin/legal-documents' },
    { label: 'AI usage ledger', path: '/admin/ai-usage' },
    { label: 'AI Purpose Routes', path: '/admin/ai-purpose-routes' },
    { label: 'Performance diagnostics', path: '/admin/performance-diagnostics' }
  ];

  readonly createAdminForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', [Validators.required]],
    password: ['', [Validators.required]]
  });

  readonly createPartnerForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    slug: [''],
    accountType: ['company' as 'university' | 'company', [Validators.required]],
    contactEmail: [''],
    openEnrollment: [false],
    createPortalUser: [true],
    portalName: [''],
    portalEmail: [''],
    portalPassword: ['']
  });

  readonly updateUsernameForm = this.fb.nonNullable.group({
    newEmail: ['', [Validators.required, Validators.email]]
  });

  readonly changePasswordForm = this.fb.nonNullable.group({
    oldPassword: ['', [Validators.required]],
    newPassword: ['', [Validators.required]]
  });

  readonly addSpecializationForm = this.fb.nonNullable.group({
    roleSlug: ['', [Validators.required]],
    name: ['', [Validators.required]],
    slug: ['']
  });

  readonly populateSkillsForm = this.fb.nonNullable.group({
    careerRoleSlugs: [''],
    locations: ['India,US,Remote'],
    forceRefresh: [false],
    updateExistingJobAspects: [true]
  });

  readonly rebalanceSkillTaxonomyForm = this.fb.nonNullable.group({
    careerRoleSlugs: [''],
    dryRun: [false],
    createGeneralistSpec: [true],
    repopulateAfterRebalance: [true]
  });

  readonly populateResourcesForm = this.fb.nonNullable.group({
    careerRoleSlugs: [''],
    providers: ['youtube,github,mdn,microsoft_learn'],
    maxQueriesPerRole: [50, [Validators.required, Validators.min(1)]],
    maxResourcesPerQuery: [8, [Validators.required, Validators.min(1)]],
    maxEstimatedHours: [0.5, [Validators.required, Validators.min(0.1)]],
    recentPublishedWithinDays: [90, [Validators.required, Validators.min(1)]],
    useAiEnrichment: [true],
    isCorrectionRun: [false],
    skipRecentlyIngested: [true],
    ingestionGracePeriodDays: [7, [Validators.required, Validators.min(1)]],
    publishHighConfidence: [true],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]],
    autoEnrich: [false]
  });

  readonly populateVerifiedRoleResourcesForm = this.fb.nonNullable.group({
    careerRoleSlugs: [''],
    providers: ['youtube,github,mdn,microsoft_learn'],
    minimumVerifiedResourcesPerSkill: [2, [Validators.required, Validators.min(1)]],
    maxSkillsToProcess: [0, [Validators.required, Validators.min(0)]],
    maxResourcesPerQuery: [8, [Validators.required, Validators.min(1)]],
    maxEstimatedHours: [0.5, [Validators.required, Validators.min(0.1)]],
    recentPublishedWithinDays: [90, [Validators.required, Validators.min(1)]],
    skipRecentlyIngested: [true],
    ingestionGracePeriodDays: [7, [Validators.required, Validators.min(1)]],
    autoEnrich: [false],
    useAiEnrichment: [true],
    isCorrectionRun: [false],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]]
  });

  readonly collectProviderCatalogForm = this.fb.nonNullable.group({
    providers: ['leet_code,hackerrank,kaggle'],
    maxItemsPerProvider: [250, [Validators.required, Validators.min(1), Validators.max(5000)]]
  });

  readonly refreshJobAspectsForm = this.fb.nonNullable.group({
    careerRoleSlugs: [''],
    locations: ['India,US,Remote'],
    fullRefreshAllRoles: [false],
    isCorrectionRun: [false]
  });

  readonly enrichResourcesForm = this.fb.nonNullable.group({
    runId: [''],
    useAiEnrichment: [true],
    isCorrectionRun: [false],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]]
  });

  /** Same options as "Enrich Resources" but uses POST …/enrich/queue (global pending rows). */
  readonly queueStagedResourceEnrichmentForm = this.fb.nonNullable.group({
    useAiEnrichment: [true],
    isCorrectionRun: [false],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]]
  });

  /** POST …/enrich/commit-enriched — sync commit of enriched staged rows. */
  readonly commitEnrichedStagedResourcesForm = this.fb.nonNullable.group({
    runId: [''],
    isCorrectionRun: [false],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]]
  });

  readonly reprocessPublishedResourcesForm = this.fb.nonNullable.group({
    roleSlugs: [''],
    useAiEnrichment: [true],
    publishThreshold: [0.45, [Validators.required, Validators.min(0), Validators.max(1)]],
    strictActiveOnly: [false],
    dryRun: [false]
  });

  readonly getRunForm = this.fb.nonNullable.group({
    runId: ['', [Validators.required]]
  });

  readonly getResourceRunForm = this.fb.nonNullable.group({
    runId: ['', [Validators.required]]
  });

  readonly listRunsForm = this.fb.nonNullable.group({
    page: [1, [Validators.required, Validators.min(1)]],
    pageSize: [20, [Validators.required, Validators.min(1)]],
    status: ['']
  });

  readonly reconcileYouTubeLanguagesForm = this.fb.nonNullable.group({
    publishedOnly: [false],
    pageSize: [100, [Validators.required, Validators.min(1), Validators.max(500)]]
  });

  readonly clearPatternForm = this.fb.nonNullable.group({
    pattern: ['', [Validators.required]]
  });

  readonly migrateCareerCatalogForm = this.fb.nonNullable.group({
    stageConnectionString: ['', [Validators.required]],
    productionConnectionString: ['', [Validators.required]],
    dryRun: [true]
  });

  readonly backfillResourcePrerequisitesForm = this.fb.nonNullable.group({
    resourceStatus: [''],
    maxResources: [50, [Validators.required, Validators.min(1), Validators.max(2000)]],
    scanPageSize: [200, [Validators.required, Validators.min(10), Validators.max(500)]]
  });

  readonly clearKeyForm = this.fb.nonNullable.group({
    key: ['', [Validators.required]]
  });

  readonly listPromotionsForm = this.fb.nonNullable.group({
    page: [1, [Validators.required, Validators.min(1)]],
    pageSize: [20, [Validators.required, Validators.min(1)]],
    code: [''],
    triggerType: [''],
    tag: ['']
  });

  readonly promotionForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    description: [''],
    code: [''],
    triggerType: [
      'promo_code' as 'promo_code' | 'automatic',
      [Validators.required]
    ],
    type: [
      'percentage' as 'percentage' | 'fixed_amount',
      [Validators.required]
    ],
    value: [0, [Validators.required, Validators.min(0)]],
    isActive: [true],
    eligibility: [
      'all_users' as 'all_users' | 'first_purchase_only' | 'specific_user_tags',
      [Validators.required]
    ],
    validFrom: [''],
    validUntil: [''],
    maxUsages: [null as number | null],
    maxUsagesPerLearner: [1 as number | null],
    minOrderAmount: [0 as number | null],
    applicableServiceTypes: [[] as string[]]
  });


  async ngOnInit(): Promise<void> {
    await this.loadAlerts();
    if (this.auth.isAdmin()) {
      await this.refreshLatestIngestionBadges();
    }
  }

  async refreshLatestIngestionBadges(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.listPopulationRuns(1, 100));
      const runs = response.data ?? [];

      this.latestSweepRun = this.pickLatestRunByOperation(runs, 'zero_resource_skill_sweep');
      this.latestIngestionBadgeUpdatedAt = new Date();
    });
  }

  runStatusColor(status: CareerRolePopulationRunResponse['status']): string {
    switch (status) {
      case 'completed':
        return '#15803d';
      case 'failed':
        return '#b91c1c';
      case 'in_progress':
        return '#1d4ed8';
      case 'queued':
      case 'awaiting_batch':
        return '#7c3aed';
      default:
        return '#64748b';
    }
  }

  private pickLatestRunByOperation(
    runs: CareerRolePopulationRunResponse[],
    operationType: CareerRolePopulationRunResponse['operationType']
  ): CareerRolePopulationRunResponse | null {
    return runs
      .filter(r => r.operationType === operationType)
      .sort((a, b) => new Date(b.queuedAt).getTime() - new Date(a.queuedAt).getTime())[0] ?? null;
  }

  async loadAlerts(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.getAdminNotifications(20, 0));
      this.activeAlerts = response.filter(n => !n.isSeen && (!n.snoozedUntil || new Date(n.snoozedUntil) < new Date()));
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }

  async dismissAlert(alertId: string): Promise<void> {
    await this.run(async () => {
      await firstValueFrom(this.api.markAdminNotificationAsSeen(alertId));
      this.activeAlerts = this.activeAlerts.filter(a => a.id !== alertId);
    });
  }

  async snoozeAlert(alertId: string, minutes: number = 1440): Promise<void> {
    await this.run(async () => {
      await firstValueFrom(this.api.snoozeAdminNotification(alertId, minutes));
      this.activeAlerts = this.activeAlerts.filter(a => a.id !== alertId);
    });
  }

  async createAdminUser(): Promise<void> {
    await this.run(async () => {
      await firstValueFrom(this.api.createAdminUser(this.createAdminForm.getRawValue()));
      this.lastResponse = '{"message":"Admin user created"}';
    });
  }

  async createPartnerAccount(): Promise<void> {
    await this.run(async () => {
      const raw = this.createPartnerForm.getRawValue();
      if (raw.createPortalUser) {
        if (!raw.portalName.trim() || !raw.portalEmail.trim() || !raw.portalPassword.trim()) {
          throw new Error('Portal user name, email, and password are required.');
        }
      }
      const payload = {
        name: raw.name.trim(),
        slug: raw.slug.trim() || undefined,
        accountType: raw.accountType,
        contactEmail: raw.contactEmail.trim() || undefined,
        openEnrollment: raw.openEnrollment,
        portalUser: raw.createPortalUser
          ? {
              name: raw.portalName.trim(),
              email: raw.portalEmail.trim(),
              password: raw.portalPassword
            }
          : undefined
      };
      const created = await firstValueFrom(this.api.createAccount(payload));
      this.lastResponse = JSON.stringify(created, null, 2);
      this.createPartnerForm.reset({
        name: '',
        slug: '',
        accountType: 'company',
        contactEmail: '',
        openEnrollment: false,
        createPortalUser: true,
        portalName: '',
        portalEmail: '',
        portalPassword: ''
      });
    });
  }

  async updateUsername(): Promise<void> {
    await this.run(async () => {
      await firstValueFrom(this.api.updateUsername(this.updateUsernameForm.controls.newEmail.getRawValue()));
      this.lastResponse = '{"message":"Username updated"}';
    });
  }

  async changePassword(): Promise<void> {
    await this.run(async () => {
      await firstValueFrom(this.api.changePassword(this.changePasswordForm.getRawValue()));
      this.lastResponse = '{"message":"Password changed"}';
    });
  }

  async addSpecialization(): Promise<void> {
    await this.run(async () => {
      const raw = this.addSpecializationForm.getRawValue();
      const response = await firstValueFrom(
        this.api.addSpecialization(raw.roleSlug, {
          name: raw.name,
          slug: raw.slug.trim() ? raw.slug : null,
          inheritFromLowerSeniority: true,
          overwriteExistingSkills: false,
          maxSkillsPerSeniority: 8
        })
      );
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async populateSkills(): Promise<void> {
    await this.run(async () => {
      const raw = this.populateSkillsForm.getRawValue();
      const response = await firstValueFrom(
        this.api.populateSkills({
          careerRoleSlugs: this.csvToList(raw.careerRoleSlugs),
          locations: this.csvToList(raw.locations),
          forceRefresh: raw.forceRefresh,
          updateExistingJobAspects: raw.updateExistingJobAspects,
          maxAiTokensPerRun: null,
          maxAiCostUsdPerRun: null,
          isCorrectionRun: false
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async reconcileYouTubeLanguages(dryRun: boolean): Promise<void> {
    if (!dryRun) {
      const confirmed = confirm(
        'Archive YouTube tutorials whose detected language is outside the allowed list (default en/hi)? This updates resource status and cannot be undone from this screen.'
      );
      if (!confirmed) {
        return;
      }
    }

    await this.run(async () => {
      const raw = this.reconcileYouTubeLanguagesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.reconcileYouTubeTutorialLanguages({
          dryRun,
          publishedOnly: raw.publishedOnly,
          pageSize: raw.pageSize
        })
      );
      this.lastResponse = JSON.stringify(response, null, 2);

      if (dryRun) {
        this.lastYouTubeLanguageReconcile = response;
        this.snackBar.open(
          `Dry run: scanned ${response.scanned}, kept ${response.kept}, archived ${response.archived}, errors ${response.errors}`,
          'Close',
          { duration: 7000 }
        );
        return;
      }

      if (!response.runId) {
        this.snackBar.open('Archive reconcile did not return a run id.', 'Close', { duration: 5000 });
        return;
      }

      this.snackBar.open(`Archive reconcile queued. Run ${response.runId}`, 'OK', { duration: 6000 });
      const run = await this.pollYouTubeReconcileRun(response.runId);
      this.lastRun = run;
      this.lastResponse = JSON.stringify(run, null, 2);

      const summary = run.youtubeLanguageReconcileSummary;
      if (summary) {
        this.lastYouTubeLanguageReconcile = {
          runId: response.runId,
          scanned: summary.scanned,
          kept: summary.kept,
          archived: summary.archived,
          errors: summary.errors,
          archivedResourceIds: summary.archivedResourceIds,
          errorMessages: summary.errorMessages
        };
        this.snackBar.open(
          `Reconcile ${run.status}: scanned ${summary.scanned}, kept ${summary.kept}, archived ${summary.archived}, errors ${summary.errors}`,
          'Close',
          { duration: 7000 }
        );
      } else if (run.status === 'failed') {
        this.snackBar.open(`Reconcile failed: ${run.failureReason ?? 'unknown error'}`, 'Close', { duration: 7000 });
      }
    });
  }

  private async pollYouTubeReconcileRun(runId: string): Promise<CareerRolePopulationRunResponse> {
    const terminalStatuses = new Set(['completed', 'failed']);
    while (true) {
      const run = await firstValueFrom(this.api.getPopulationRun(runId));
      if (terminalStatuses.has(run.status)) {
        return run;
      }
      await new Promise(resolve => setTimeout(resolve, 5000));
    }
  }

  async rebalanceSkillTaxonomy(): Promise<void> {
    await this.run(async () => {
      const raw = this.rebalanceSkillTaxonomyForm.getRawValue();
      const response = await firstValueFrom(
        this.api.rebalanceSkillTaxonomy({
          careerRoleSlugs: this.csvToList(raw.careerRoleSlugs),
          dryRun: raw.dryRun,
          createGeneralistSpec: raw.createGeneralistSpec,
          repopulateAfterRebalance: raw.repopulateAfterRebalance
        })
      );
      this.lastResponse = JSON.stringify(response, null, 2);

      if (response.dryRun) {
        this.snackBar.open(
          `Dry run: ${response.rolesUpdated ?? 0} updated, ${response.rolesSkipped ?? 0} skipped`,
          'OK',
          { duration: 6000 }
        );
        return;
      }

      if (response.repopulateRunId) {
        const run = await firstValueFrom(this.api.getPopulationRun(response.repopulateRunId));
        this.lastRun = run;
        this.snackBar.open(
          `Rebalance queued for ${response.rolesRequested} role(s). Run ${response.repopulateRunId}`,
          'OK',
          { duration: 6000 }
        );
      }
    });
  }

  async getRun(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.getPopulationRun(this.getRunForm.controls.runId.getRawValue()));
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async populateResources(): Promise<void> {
    await this.run(async () => {
      const raw = this.populateResourcesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.populateResources({
          careerRoleSlugs: this.csvToList(raw.careerRoleSlugs),
          providers: this.csvToList(raw.providers),
          maxQueriesPerRole: raw.maxQueriesPerRole,
          maxResourcesPerQuery: raw.maxResourcesPerQuery,
          maxEstimatedHours: raw.maxEstimatedHours,
          recentPublishedWithinDays: raw.recentPublishedWithinDays,
          useAiEnrichment: raw.useAiEnrichment,
          isCorrectionRun: raw.isCorrectionRun,
          skipRecentlyIngested: raw.skipRecentlyIngested,
          ingestionGracePeriodDays: raw.ingestionGracePeriodDays,
          publishHighConfidence: raw.publishHighConfidence,
          publishThreshold: raw.publishThreshold,
          autoEnrich: raw.autoEnrich
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async populateVerifiedRoleResources(): Promise<void> {
    await this.run(async () => {
      const raw = this.populateVerifiedRoleResourcesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.populateVerifiedRoleResources({
          careerRoleSlugs: this.csvToList(raw.careerRoleSlugs),
          providers: this.csvToList(raw.providers),
          minimumVerifiedResourcesPerSkill: raw.minimumVerifiedResourcesPerSkill,
          maxSkillsToProcess: raw.maxSkillsToProcess,
          maxResourcesPerQuery: raw.maxResourcesPerQuery,
          maxEstimatedHours: raw.maxEstimatedHours,
          recentPublishedWithinDays: raw.recentPublishedWithinDays,
          skipRecentlyIngested: raw.skipRecentlyIngested,
          ingestionGracePeriodDays: raw.ingestionGracePeriodDays,
          autoEnrich: raw.autoEnrich,
          useAiEnrichment: raw.useAiEnrichment,
          isCorrectionRun: raw.isCorrectionRun,
          publishThreshold: raw.publishThreshold
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async collectProviderCatalog(): Promise<void> {
    await this.run(async () => {
      const raw = this.collectProviderCatalogForm.getRawValue();
      const response = await firstValueFrom(
        this.api.collectProviderCatalog({
          providers: this.csvToList(raw.providers),
          maxItemsPerProvider: raw.maxItemsPerProvider
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async refreshJobAspects(): Promise<void> {
    await this.run(async () => {
      const raw = this.refreshJobAspectsForm.getRawValue();
      const response = await firstValueFrom(
        this.api.refreshJobAspects({
          careerRoleSlugs: this.csvToList(raw.careerRoleSlugs),
          locations: this.csvToList(raw.locations),
          fullRefreshAllRoles: raw.fullRefreshAllRoles,
          isCorrectionRun: raw.isCorrectionRun
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async enrichResources(): Promise<void> {
    await this.run(async () => {
      const raw = this.enrichResourcesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.enrichResources({
          runId: raw.runId,
          useAiEnrichment: raw.useAiEnrichment,
          isCorrectionRun: raw.isCorrectionRun,
          publishThreshold: raw.publishThreshold
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async queueStagedResourceEnrichment(): Promise<void> {
    await this.run(async () => {
      const raw = this.queueStagedResourceEnrichmentForm.getRawValue();
      const response = await firstValueFrom(
        this.api.queueStagedResourceEnrichment({
          useAiEnrichment: raw.useAiEnrichment,
          isCorrectionRun: raw.isCorrectionRun,
          publishThreshold: raw.publishThreshold
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async commitEnrichedStagedResources(): Promise<void> {
    await this.run(async () => {
      const raw = this.commitEnrichedStagedResourcesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.commitEnrichedStagedResources({
          runId: raw.runId.trim(),
          isCorrectionRun: raw.isCorrectionRun,
          publishThreshold: raw.publishThreshold
        })
      );
      this.lastRun = null;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async reprocessPublishedResources(): Promise<void> {
    await this.run(async () => {
      const raw = this.reprocessPublishedResourcesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.reprocessPublishedResources({
          roleSlugs: this.csvToList(raw.roleSlugs),
          useAiEnrichment: raw.useAiEnrichment,
          publishThreshold: raw.publishThreshold,
          strictActiveOnly: raw.strictActiveOnly,
          dryRun: raw.dryRun
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async getResourceRun(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(
        this.api.getResourcePopulationRun(this.getResourceRunForm.controls.runId.getRawValue())
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async listRuns(): Promise<void> {
    await this.run(async () => {
      const raw = this.listRunsForm.getRawValue();
      const response = await firstValueFrom(
        this.api.listPopulationRuns(raw.page, raw.pageSize, raw.status.trim() || undefined)
      );
      this.listedRuns = response.data;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  openRoleListPage(): void {
    void this.router.navigate(['/admin/career-roles']);
  }

  openResourceManagementPage(): void {
    void this.router.navigate(['/admin/resource-management']);
  }

  openSkillDeDuplicationPage(): void {
    void this.router.navigate(['/admin/skill-deduplication']);
  }

  openArchivedSkillsPage(): void {
    void this.router.navigate(['/admin/archived-skills']);
  }

  openCatalogCollectionPage(): void {
    void this.router.navigate(['/admin/catalog-collection']);
  }

  openZeroResourceSweepPage(): void {
    void this.router.navigate(['/admin/zero-resource-sweep']);
  }

  openTopicDependencyBackfillPage(): void {
    void this.router.navigate(['/admin/topic-dependency-backfill']);
  }

  openBackgroundJobsPage(): void {
    void this.router.navigate(['/admin/background-jobs']);
  }

  openLessonsPage(): void {
    void this.router.navigate(['/admin/lessons']);
  }

  openCareerChallengesPage(): void {
    void this.router.navigate(['/admin/career-challenges']);
  }

  async getCacheStats(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.getCacheStats());
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async triggerMongoBackup(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.triggerMongoBackup());
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async triggerAutomatedSkillResourcePopulation(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.triggerAutomatedSkillResourcePopulation());
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async migrateCareerCatalog(): Promise<void> {
    if (this.migrateCareerCatalogForm.invalid) {
      this.migrateCareerCatalogForm.markAllAsTouched();
      return;
    }

    const raw = this.migrateCareerCatalogForm.getRawValue();
    if (raw.stageConnectionString.trim() === raw.productionConnectionString.trim()) {
      this.snackBar.open('Stage and production connection strings must be different.', 'Dismiss', {
        duration: 4000
      });
      return;
    }

    await this.run(async () => {
      const response = await firstValueFrom(
        this.api.migrateCareerCatalog({
          stageConnectionString: raw.stageConnectionString.trim(),
          productionConnectionString: raw.productionConnectionString.trim(),
          dryRun: raw.dryRun
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
      this.snackBar.open(
        raw.dryRun
          ? `Dry-run migration queued (${response.id}).`
          : `Career catalog migration queued (${response.id}).`,
        'Dismiss',
        { duration: 5000 }
      );
    });
  }

  async backfillResourcePrerequisites(): Promise<void> {
    if (this.backfillResourcePrerequisitesForm.invalid) {
      this.backfillResourcePrerequisitesForm.markAllAsTouched();
      return;
    }

    await this.run(async () => {
      const raw = this.backfillResourcePrerequisitesForm.getRawValue();
      const response = await firstValueFrom(
        this.api.backfillResourcePrerequisites({
          resourceStatus: (raw.resourceStatus || undefined) as
            | 'draft'
            | 'published'
            | 'review_required'
            | 'validation_failed'
            | 'inactive'
            | 'archived'
            | undefined,
          maxResources: raw.maxResources,
          scanPageSize: raw.scanPageSize
        })
      );
      this.lastRun = response;
      this.lastResponse = JSON.stringify(response, null, 2);
      this.snackBar.open(`Prerequisites backfill queued (${response.id}).`, 'Dismiss', {
        duration: 5000
      });
    });
  }

  async clearAllCache(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.clearAllCache());
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async clearPattern(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.clearCacheByPattern(this.clearPatternForm.controls.pattern.getRawValue()));
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async listPromotions(): Promise<void> {
    await this.run(async () => {
      const raw = this.listPromotionsForm.getRawValue();
      const response = await firstValueFrom(
        this.api.listPromotions(raw.page, raw.pageSize, {
          code: raw.code || undefined,
          triggerType: raw.triggerType || undefined,
          tag: raw.tag || undefined
        })
      );
      this.listedPromotions = response.data;
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async savePromotion(): Promise<void> {
    await this.run(async () => {
      const raw = this.promotionForm.value as Partial<PromotionEntity>;
      if (this.selectedPromotion) {
        const response = await firstValueFrom(this.api.updatePromotion(this.selectedPromotion.id, raw));
        this.lastResponse = JSON.stringify(response, null, 2);
      } else {
        const response = await firstValueFrom(this.api.createPromotion(raw));
        this.lastResponse = JSON.stringify(response, null, 2);
      }
      this.selectedPromotion = null;
      this.promotionForm.reset();
      await this.listPromotions();
    });
  }

  selectPromotion(promotion: PromotionEntity): void {
    this.selectedPromotion = promotion;
    this.promotionForm.patchValue({
      name: promotion.name,
      description: promotion.description,
      code: promotion.code,
      triggerType: promotion.triggerType,
      type: promotion.type,
      value: promotion.value,
      isActive: promotion.isActive,
      eligibility: promotion.eligibility,
      validFrom: promotion.validFrom ? new Date(promotion.validFrom).toISOString().split('T')[0] : '',
      validUntil: promotion.validUntil ? new Date(promotion.validUntil).toISOString().split('T')[0] : '',
      maxUsages: promotion.maxUsages,
      maxUsagesPerLearner: promotion.maxUsagesPerLearner,
      minOrderAmount: promotion.minOrderAmount,
      applicableServiceTypes: promotion.applicableServiceTypes || []
    });
  }

  resetPromotionForm(): void {
    this.selectedPromotion = null;
    this.promotionForm.reset({
      triggerType: 'promo_code',
      type: 'percentage',
      isActive: true,
      eligibility: 'all_users',
      maxUsagesPerLearner: 1,
      value: 0,
      applicableServiceTypes: []
    });
  }

  async clearKey(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.clearCacheByKey(this.clearKeyForm.controls.key.getRawValue()));
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async clearDashboardCache(): Promise<void> {
    await this.run(async () => {
      const response = await firstValueFrom(this.api.clearDashboardCache());
      this.lastResponse = JSON.stringify(response, null, 2);
    });
  }

  async logout(): Promise<void> {
    await this.auth.logout();
    await this.router.navigate(['/login']);
  }

  private async run(action: () => Promise<void>): Promise<void> {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.lastError = '';
    try {
      await action();
      this.snackBar.open('Request completed', 'Close', { duration: 2200 });
    } catch (error) {
      this.lastError = this.errorMessage(error);
      this.snackBar.open(this.lastError, 'Close', { duration: 3200 });
    } finally {
      this.loading = false;
    }
  }

  private csvToList(value: string): string[] {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiErrorShape;
      if (body?.errors) {
        const first = Object.values(body.errors)[0];
        if (typeof first === 'string' && first.length > 0) {
          return first;
        }
      }
      return body?.title ?? `Request failed (${error.status})`;
    }
    return 'Unexpected error';
  }
}
